import { normaliseText } from '@1trading/ts-common/utils';
import { z } from 'zod';

import i18n from '@/lib/i18n';
import { hasListedCompanyIdentifier, institutionDetailsV2ListedCompanySchema } from '@/services/api/profile';
import { addFieldIssue } from '@/utils/field-issues';

export type InstitutionListedCompany = {
  // Only asked about another company's listing, such as a parent's
  name: string;
  mic: string;
  ticker: string;
  isin: string;
};

export const INSTITUTION_LISTED_COMPANY_DEFAULTS: InstitutionListedCompany = { name: '', mic: '', ticker: '', isin: '' };

// The inputs stay text, so each identifier is checked against the API's schema, which reads a blank one as not given
export const institutionListedCompanySchema = z.object({ name: z.string(), mic: z.string(), ticker: z.string(), isin: z.string() });

const requireValidIdentifiers = (listedCompany: InstitutionListedCompany, context: z.RefinementCtx) => {
  for (const key of ['mic', 'ticker', 'isin'] as const) {
    addFieldIssue(context, [key], institutionDetailsV2ListedCompanySchema.shape[key], listedCompany[key]);
  }
};

// Either input answers it, so both show the error
const requireIsinOrTicker = ({ ticker, isin }: InstitutionListedCompany, context: z.RefinementCtx) => {
  if (hasListedCompanyIdentifier({ ticker: normaliseText(ticker), isin: normaliseText(isin) })) return;

  for (const key of ['isin', 'ticker']) {
    context.addIssue({ code: 'custom', path: [key], message: i18n.t('institutionDetails.ownership.errors.isinOrTicker') });
  }
};

export const withListedCompanyRefinements = <Schema extends z.ZodType<InstitutionListedCompany>>(schema: Schema) =>
  schema.superRefine(requireValidIdentifiers).superRefine(requireIsinOrTicker);
